// UBO Quote Theme - Based on HubSpot ubo-quotes-theme

export const uboTheme = {
  colors: {
    // UBO Brand Colors
    primary: 'rgb(0, 7, 236)',
    secondary: '#070744',
    accent: 'rgb(0, 7, 236)',
    textOnAccent: '#ffffff',
    textOnBrand: '#ffffff',
    strokeOnBrand: '#ffffff',
    tableHeaderBg: '#e6e6eb',
    
    // UBX Brand Colors (alternative)
    ubx: {
      primary: '#060b1f',
      secondary: '#060b1f',
      accent: '#4bfae5',
      /** Randen in investeringstabellen (editor + PDF) */
      investmentTableRule: '#4bfbe7',
      textOnAccent: '#060b1f',
      /** Naam-/functielabels onder teamfoto's */
      teamLabelBackground: '#4bfae5',
      teamLabelText: '#060b1f',
    },
  },
  
  fonts: {
    primary: 'september, sans-serif',
    secondary: 'museo-sans, sans-serif',
    primaryFallback: 'september, sans-serif',
    secondaryFallback: 'museo-sans, sans-serif',
  },
  
  // A4 Landscape dimensions
  page: {
    width: '297mm',
    height: '210mm',
    padding: '50px',
  },
  
  typography: {
    h1: {
      fontSize: '62px',
      fontWeight: 900,
      textTransform: 'uppercase',
      marginBottom: '25px',
    },
    h2: {
      fontSize: '32px',
      fontWeight: 900,
      textTransform: 'uppercase',
    },
    h3: {
      fontSize: '24px',
      fontWeight: 900,
      textTransform: 'uppercase',
    },
    h4: {
      fontSize: '20px',
      fontWeight: 900,
      textTransform: 'uppercase',
    },
    body: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
  },
};

export type ThemePalette = {
  primary: string;
  secondary: string;
  accent: string;
  tableHeaderBg: string;
  /** Alleen UBX: 2px-lijnen rond investeringstabellen */
  investmentTableRule?: string;
  teamLabelBackground?: string;
  teamLabelText?: string;
};

export function themeColors(variant?: 'ubo' | 'ubx'): ThemePalette {
  if (variant === 'ubx') {
    const u = uboTheme.colors.ubx;
    return {
      primary: u.primary,
      secondary: u.secondary,
      accent: u.accent,
      tableHeaderBg: uboTheme.colors.tableHeaderBg,
      investmentTableRule: u.investmentTableRule,
      teamLabelBackground: u.teamLabelBackground,
      teamLabelText: u.teamLabelText,
    };
  }
  return {
    primary: uboTheme.colors.primary,
    secondary: uboTheme.colors.secondary,
    accent: uboTheme.colors.accent,
    tableHeaderBg: uboTheme.colors.tableHeaderBg,
  };
}

// Page style options (matching HubSpot editor module)
export type PageStyle =
  | 'default'
  | 'juridisch'
  | 'primary'
  | 'merkidentiteit'
  | 'ontwikkelfase'
  | 'positionering'
  | 'strategie';

export const pageStyles: Record<PageStyle, { label: string; bgColor: string; textColor: string; hasBranding?: boolean }> = {
  default: { label: 'Wit', bgColor: '#ffffff', textColor: '#070744' },
  /** Wit met compacte tekst en artikelnummering: verwerkersovereenkomst, voorwaarden, bijlagen. */
  juridisch: { label: 'Juridisch', bgColor: '#ffffff', textColor: '#070744' },
  primary: { label: 'Blauw', bgColor: '#050ced', textColor: '#ffffff' },
  merkidentiteit: { label: 'Merkidentiteit', bgColor: '#050ced', textColor: '#ffffff', hasBranding: true },
  ontwikkelfase: { label: 'Ontwikkelfase', bgColor: '#050ced', textColor: '#ffffff', hasBranding: true },
  positionering: { label: 'Positionering', bgColor: '#050ced', textColor: '#ffffff', hasBranding: true },
  strategie: { label: 'Strategie', bgColor: '#050ced', textColor: '#ffffff', hasBranding: true },
};

/** Witte pagina: donkere tekst en logo's zonder witfilter. */
export function isLightPageStyle(style: PageStyle | string | null | undefined): boolean {
  return (pageStyles[style as PageStyle] ?? pageStyles.default).bgColor === '#ffffff';
}

/** UBO “blauwe” contentpagina-achtergrond (editor / sidebar). */
const UBO_PAGE_BLUE_BG = '#050ced';

/**
 * Voor UBX-template: blauwe paginastijlen → UBX-donker (#060b1f). Wit/licht ongewijzigd.
 */
export function resolvePageStyleConfig(
  style: PageStyle,
  brand: 'ubo' | 'ubx'
): { label: string; bgColor: string; textColor: string; hasBranding?: boolean } {
  const base = pageStyles[style] ?? pageStyles.default;
  if (brand !== 'ubx' || base.bgColor !== UBO_PAGE_BLUE_BG) {
    return base;
  }
  return { ...base, bgColor: uboTheme.colors.ubx.primary };
}

// Pricing table types
export type PricingType = 'hourly' | 'fixed' | 'recurring';

export const pricingTypes: Record<PricingType, { label: string; columns: string[] }> = {
  hourly: { 
    label: 'Uurtarief', 
    columns: ['Omschrijving', 'Prijs excl. BTW', 'Aantal uren', 'Subtotaal'] 
  },
  fixed: { 
    label: 'Vast bedrag', 
    columns: ['Omschrijving'] 
  },
  recurring: { 
    label: 'Terugkerend', 
    columns: ['Omschrijving', 'Prijs excl. BTW'] 
  },
};
